import { Intent, TagInput, TagInputAddMethod } from '../ui';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useFormik } from 'formik';
import React, { Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { useCreateBrief } from '../../hooks/useBriefs';
import { sanitizeKeyword } from '../../services/briefs';
import { Account } from '../../services/models/account';
import { BriefKeywordType } from '../../services/models/brief-keyword-type';
import { BriefLanguage, supportedBriefLanguages } from '../../services/models/brief-language';
import Input from '../ui/Input';

interface AddBriefPanelProps {
  account: Account;
  panelOpen: boolean;
  onClosePanel: () => void;
}

export default function AddBriefPanel({ account, panelOpen, onClosePanel }: AddBriefPanelProps) {
  const history = useHistory();

  const productFeatures = account.product.features;
  const { createBrief, isCreating } = useCreateBrief();

  const handleClosePanel = () => {
    if (!isCreating) {
      formik.resetForm();
      onClosePanel();
    }
  };

  // TODO(Jose): Improve topic selection by preventing setting an exclusion
  const handleTopicsChange = (values: React.ReactNode[]) => {
    let keywords: string[] = values.map(k => (k as string).valueOf());
    formik.setFieldValue('topics', keywords);
  };

  const handleFiltersChange = (values: React.ReactNode[]) => {
    let keywords: string[] = values.map(k => (k as string).valueOf());
    formik.setFieldValue('filters', keywords);
  };

  const handleExclusionsChange = (values: React.ReactNode[]) => {
    let keywords: string[] = values.map(k => (k as string).valueOf());
    formik.setFieldValue('exclusions', keywords);
  };

  // `values` is the would-be full list (TagInput contract), so this enforces
  // the plan's keyword cap on the total — the legacy Blueprint check only saw
  // the newly-typed batch and never actually enforced the limit.
  const handleAddNewKeyword = (values: string[], method: TagInputAddMethod) => {
    return values.length <= productFeatures.keywordsPerBrief;
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      language: BriefLanguage.English,
      topics: [] as string[],
      filters: [] as string[],
      exclusions: [] as string[],
    },
    validationSchema: yup.object().shape({
      name: yup.string().required('Brief Name is required'),
      language: yup.string().required('Brief language is required'),
      topics: yup
        .array()
        .of(yup.string())
        .min(1)
        .max(productFeatures.keywordsPerBrief)
        .required('A brief topic is required'),
    }),
    onSubmit: async values => {
      const topicKeywords = values.topics.map(t => ({
        type: BriefKeywordType.Topic,
        exactMatch: t.startsWith('"'),
        keyword: sanitizeKeyword(t),
      }));

      const filterKeywords = values.filters.map(t => ({
        type: BriefKeywordType.Filter,
        exactMatch: t.startsWith('"'),
        keyword: sanitizeKeyword(t),
      }));

      const exclusionsKeywords = values.exclusions.map(t => ({
        type: BriefKeywordType.Exclusion,
        exactMatch: t.startsWith('"'),
        keyword: sanitizeKeyword(t),
      }));

      // Navigate from the mutation result id (the legacy effect matched the
      // new brief by name, which picked the wrong brief on duplicate names).
      const newBrief = await createBrief({
        name: values.name,
        keywords: [...topicKeywords, ...filterKeywords, ...exclusionsKeywords],
        draft: false,
        language: values.language,
      });

      if (newBrief) {
        onClosePanel();
        history.push(`/dashboard/brief/${newBrief.id}`);
      }
    },
  });

  return (
    <Transition show={panelOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 overflow-hidden" onClose={handleClosePanel}>
        <div className="absolute inset-0 overflow-hidden">
          <TransitionChild
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="absolute inset-0 bg-gray-500/75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex sm:pl-16">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <DialogPanel className="w-screen max-w-2xl">
                {/* onSubmit makes Enter submit from text fields (TagInput
                    preventDefaults its own Enter, so chip commits don't). */}
                <form
                  onSubmit={formik.handleSubmit}
                  className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll"
                >
                  {/* Main Content */}
                  <div className="flex-1">
                    {/* Header */}
                    <div className="px-4 py-6 bg-gray-50 sm:px-6">
                      <div className="flex items-start justify-between space-x-3">
                        <div className="space-y-1">
                          <DialogTitle className="text-lg font-medium text-gray-900">
                            New brief
                          </DialogTitle>
                          <p className="text-sm text-gray-500">
                            Get started by filling in the information below to create your new
                            brief.
                          </p>
                        </div>
                        <div className="h-7 flex items-center">
                          <button
                            type="button"
                            className="text-gray-400 hover:text-gray-500"
                            onClick={onClosePanel}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Divider container */}
                    <div className="py-6 space-y-6 sm:py-0 sm:space-y-0 sm:divide-y sm:divide-gray-200">
                      {/* Brief name */}
                      <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                        <div>
                          <label
                            htmlFor="brief-name"
                            className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                          >
                            Brief name
                          </label>
                        </div>
                        <div className="sm:col-span-2">
                          <Input
                            type="text"
                            name="name"
                            id="name"
                            required
                            autoComplete="off"
                            error={
                              formik.touched.name && formik.errors.name
                                ? formik.errors.name
                                : undefined
                            }
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </div>
                      </div>

                      {/* Brief Language */}
                      <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                        <div>
                          <label
                            htmlFor="brief-lang"
                            className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                          >
                            Language
                          </label>
                        </div>
                        <div className="sm:col-span-2">
                          <select
                            id="lang"
                            name="language"
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-hidden focus:ring-secondary focus:border-secondary sm:text-sm rounded-md"
                            value={formik.values.language}
                            onChange={formik.handleChange}
                          >
                            {supportedBriefLanguages.map(item => (
                              <option key={item.lang} value={item.lang}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Brief Topics */}
                      <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                        <div>
                          <label
                            htmlFor="brief-topics"
                            className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                          >
                            Topics
                          </label>
                          <p className="text-sm text-gray-500">
                            Enter related keywords on the topics you are interested
                          </p>
                        </div>
                        <div className="sm:col-span-2">
                          <TagInput
                            key="brief-topics"
                            placeholder="Separate topic keywords with commas..."
                            values={formik.values.topics}
                            onChange={handleTopicsChange}
                            onAdd={handleAddNewKeyword}
                            addOnBlur={true}
                          />
                          <p className="mt-2 text-xs text-gray-500">
                            [{formik.values.topics.length}/{productFeatures.keywordsPerBrief}]
                            keywords / phrase
                          </p>
                        </div>
                      </div>

                      {/* Brief Filters */}
                      <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                        <div>
                          <label
                            htmlFor="brief-filters"
                            className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                          >
                            Filters
                          </label>
                          <p className="text-sm text-gray-500">
                            Articles <strong>must</strong> include at least one of these words
                          </p>
                        </div>
                        <div className="sm:col-span-2">
                          <TagInput
                            key="brief-filters"
                            placeholder="Separate filter keywords with commas..."
                            values={formik.values.filters}
                            onAdd={handleAddNewKeyword}
                            onChange={handleFiltersChange}
                            addOnBlur={true}
                          />
                          <p className="mt-2 text-xs text-gray-500">
                            [{formik.values.filters.length}/{productFeatures.keywordsPerBrief}]
                            keywords / phrase
                          </p>
                        </div>
                      </div>

                      {/* Brief Exclusions */}
                      <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                        <div>
                          <label
                            htmlFor="brief-exclusions"
                            className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                          >
                            Exclusions
                          </label>
                          <p className="text-sm text-gray-500">
                            Articles must <strong>not</strong> include any of these words
                          </p>
                        </div>
                        <div className="sm:col-span-2">
                          <TagInput
                            key="brief-exclusions"
                            placeholder="Separate exclusion keywords with commas..."
                            values={formik.values.exclusions}
                            onAdd={handleAddNewKeyword}
                            onChange={handleExclusionsChange}
                            addOnBlur={true}
                          />
                          <p className="mt-2 text-xs text-gray-500">
                            [{formik.values.exclusions.length}/{productFeatures.keywordsPerBrief}]
                            keywords / phrase
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="shrink-0 px-4 border-t border-gray-200 py-5 sm:px-6">
                    <div className="space-x-3 flex justify-end">
                      <button
                        type="button"
                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-xs text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                        onClick={onClosePanel}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary disabled:opacity-50"
                        disabled={isCreating || !formik.isValid}
                      >
                        {isCreating && (
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        )}
                        Create
                      </button>
                    </div>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
